// src/pages/common/AdvancedSearchConferenciaKanbanModalContent.tsx
import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@/icons';
import SearchButton from '../../components/common/SearchButton';
import DateRangeInput from '../../components/common/DateRangeInput';
import DateInput from '../../components/common/DateInput';
import './AdvancedSearchConferenciaKanbanModalContent.css';
import { useApiCall } from '../../hooks/useApiCall';
import { showAlert } from '../../contexts/DialogContext';
import { Toast } from '../../contexts/ToastContext';
import { logger } from '@/utils/logger';

interface AdvancedSearchConferenciaKanbanModalContentProps {
  theme: 'dark' | 'light';
  onClose: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSearch: (result: any) => void;
}

const AdvancedSearchConferenciaKanbanModalContent: React.FC<AdvancedSearchConferenciaKanbanModalContentProps> = ({
  theme,
  onClose,
  onSearch,
}) => {
  const [conferenciaId, setConferenciaId] = useState('');
  const [ordemCompraId, setOrdemCompraId] = useState('');
  const [requisicaoId, setRequisicaoId] = useState('');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [dataEntrega, setDataEntrega] = useState('');
  const [dataEncerrada, setDataEncerrada] = useState('');
  const [codProduto, setCodProduto] = useState('');
  const [nomeProduto, setNomeProduto] = useState('');
  const [codCategoria, setCodCategoria] = useState('');
  const [nomeCategoria, setNomeCategoria] = useState('');
  const [prioridade, setPrioridade] = useState('');
  const [loja, setLoja] = useState('');
  const [requisitante, setRequisitante] = useState('');
  const [origem, setOrigem] = useState<'' | 'PLANEJAMENTO' | 'BALCAO'>('');
  const [contato, setContato] = useState('');
  const [nomeConsultor, setNomeConsultor] = useState('');
  const [codCliente, setCodCliente] = useState('');
  const [nomeCliente, setNomeCliente] = useState('');
  const [codFornecedor, setCodFornecedor] = useState('');
  const [cnpjFornecedor, setCnpjFornecedor] = useState('');
  const [nomeFornecedor, setNomeFornecedor] = useState('');
  const [lojaEntrega, setLojaEntrega] = useState('');
  const [preOrdemId, setPreOrdemId] = useState('');
  const [nomeComprador, setNomeComprador] = useState('');
  const [nroNotaFiscal, setNroNotaFiscal] = useState('');
  const [nomeConferente, setNomeConferente] = useState('');

  const { callApi, isLoading } = useApiCall();

  const filterValuesRef = useRef({
    conferenciaId,
    ordemCompraId,
    requisicaoId,
    dataInicio,
    dataFim,
    dataEntrega,
    dataEncerrada,
    codProduto,
    nomeProduto,
    codCategoria,
    nomeCategoria,
    prioridade,
    loja,
    requisitante,
    origem,
    contato,
    nomeConsultor,
    codCliente,
    nomeCliente,
    codFornecedor,
    cnpjFornecedor,
    nomeFornecedor,
    lojaEntrega,
    preOrdemId,
    nomeComprador,
    nroNotaFiscal,
    nomeConferente,
  });

  useEffect(() => {
    filterValuesRef.current = {
      conferenciaId,
      ordemCompraId,
      requisicaoId,
      dataInicio,
      dataFim,
      dataEntrega,
      dataEncerrada,
      codProduto,
      nomeProduto,
      codCategoria,
      nomeCategoria,
      prioridade,
      loja,
      requisitante,
      origem,
      contato,
      nomeConsultor,
      codCliente,
      nomeCliente,
      codFornecedor,
      cnpjFornecedor,
      nomeFornecedor,
      lojaEntrega,
      preOrdemId,
      nomeComprador,
      nroNotaFiscal,
      nomeConferente,
    };
  }, [
    conferenciaId,
    ordemCompraId,
    requisicaoId,
    dataInicio,
    dataFim,
    dataEntrega,
    dataEncerrada,
    codProduto,
    nomeProduto,
    codCategoria,
    nomeCategoria,
    prioridade,
    loja,
    requisitante,
    origem,
    contato,
    nomeConsultor,
    codCliente,
    nomeCliente,
    codFornecedor,
    cnpjFornecedor,
    nomeFornecedor,
    lojaEntrega,
    preOrdemId,
    nomeComprador,
    nroNotaFiscal,
    nomeConferente,
  ]);

  // Helper: mantém callApi, mas permite await (necessário para loop de allIds)
  const callApiGet = (endpoint: string) =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    new Promise<any>((resolve, reject) => {
      callApi('get', endpoint, null, {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onSuccess: (data: any) => resolve(data),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onError: (err: any) => reject(err),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any);
    });

  const handleSearch = async () => {
    const values = filterValuesRef.current;

    const hasFilter = Object.values(values).some(
      (value) => (typeof value === 'string' && value.trim() !== '') || value === 'true' || value === 'false'
    );

    if (!hasFilter) {
      await showAlert('Pelo menos um parâmetro de filtro deve ser fornecido.', { variant: 'warning' });
      return;
    }

    try {
      const baseParams = new URLSearchParams();

      if (values.conferenciaId) baseParams.append('recebimentoConferenciaId', values.conferenciaId.trim());
      if (values.ordemCompraId) baseParams.append('ordemCompraId', values.ordemCompraId.trim());
      if (values.requisicaoId) baseParams.append('requisicaoId', values.requisicaoId.trim());
      if (values.dataInicio) baseParams.append('dataInicio', values.dataInicio.trim());
      if (values.dataFim) baseParams.append('dataFim', values.dataFim.trim());
      if (values.dataEntrega) baseParams.append('dataEntrega', values.dataEntrega.trim());
      if (values.dataEncerrada) baseParams.append('dataEncerrada', values.dataEncerrada.trim());
      if (values.codProduto) baseParams.append('codProduto', values.codProduto.trim());
      if (values.nomeProduto) baseParams.append('nomeProduto', values.nomeProduto.trim());
      if (values.codCategoria) baseParams.append('codCategoria', values.codCategoria.trim());
      if (values.nomeCategoria) baseParams.append('nomeCategoria', values.nomeCategoria.trim());
      if (values.prioridade) baseParams.append('prioridade', values.prioridade.trim());
      if (values.loja) baseParams.append('loja', values.loja.trim());
      if (values.requisitante) baseParams.append('requisitante', values.requisitante.trim());
      if (values.origem) baseParams.append('origem', values.origem);
      if (values.contato) baseParams.append('contato', values.contato.trim());
      if (values.nomeConsultor) baseParams.append('nomeConsultor', values.nomeConsultor.trim());
      if (values.codCliente) baseParams.append('codCliente', values.codCliente.trim());
      if (values.nomeCliente) baseParams.append('nomeCliente', values.nomeCliente.trim());
      if (values.codFornecedor) baseParams.append('codFornecedor', values.codFornecedor.trim());
      if (values.cnpjFornecedor) baseParams.append('cnpjFornecedor', values.cnpjFornecedor.trim());
      if (values.nomeFornecedor) baseParams.append('nomeFornecedor', values.nomeFornecedor.trim());
      if (values.lojaEntrega) baseParams.append('lojaEntrega', values.lojaEntrega.trim());
      if (values.preOrdemId) baseParams.append('preOrdemId', values.preOrdemId.trim());
      if (values.nomeComprador) baseParams.append('nomeComprador', values.nomeComprador.trim());
      if (values.nroNotaFiscal) baseParams.append('nroNotaFiscal', values.nroNotaFiscal.trim());
      if (values.nomeConferente) baseParams.append('nomeConferente', values.nomeConferente.trim());

      const queryString = baseParams.toString();

      const fetchAllIds = async (section: 'rascunho' | 'finalizado'): Promise<number[]> => {
        let page = 0;
        const size = 100;
        let allIds: number[] = [];

        while (true) {
          const endpoint = `/v1/busca-inteligente/capa-gestao-conferencia?${queryString}&page=${page}&size=${size}`;
          const data = await callApiGet(endpoint);
          const sectionData = data?.[section];

          const content = sectionData?.content ?? [];
          if (!content.length) break;

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          allIds = allIds.concat(content.map((item: any) => item.id));

          const totalPages = sectionData?.page?.totalPages ?? 1;
          if (page >= totalPages - 1) break;

          page++;
        }

        return allIds;
      };

      // 1ª página para display (size 10)
      let result = await callApiGet(`/v1/busca-inteligente/capa-gestao-conferencia?${queryString}&page=0&size=10`);

      // allIds completos (somente rascunho/finalizado)
      const [rascunhoIds, finalizadoIds] = await Promise.all([fetchAllIds('rascunho'), fetchAllIds('finalizado')]);

      result = {
        ...result,
        __query: queryString, // essencial para paginação pós-busca
        rascunho: { ...result.rascunho, allIds: rascunhoIds },
        finalizado: { ...result.finalizado, allIds: finalizadoIds },
      };

      onSearch(result);
      Toast.success('Pesquisa realizada com sucesso!');
      onClose();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      logger.error('Erro ao buscar:', err);
      await showAlert(err?.response?.data?.error || err?.response?.data?.message || 'Erro ao realizar a busca.', { variant: 'error' });
    }
  };

  return (
    <div className={`advanced-search-conferencia-kanban-modal-content ${theme}`}>
      <h2>
        <FontAwesomeIcon icon={faMagnifyingGlass} /> Pesquisa Avançada
      </h2>

      <div className="search-form">
        <div className="form-inputs">
          <div className="form-row">
            <input
              type="text"
              value={requisicaoId}
              onChange={(e) => setRequisicaoId(e.target.value)}
              placeholder="Requisição"
              className="search-input"
            />
            <DateRangeInput
              dataInicio={dataInicio}
              setDataInicio={setDataInicio}
              dataFim={dataFim}
              setDataFim={setDataFim}
              theme={theme}
            />
            <input
              type="text"
              value={codProduto}
              onChange={(e) => setCodProduto(e.target.value)}
              placeholder="Código Produto"
              className="search-input"
            />
            <input
              type="text"
              value={nomeProduto}
              onChange={(e) => setNomeProduto(e.target.value)}
              placeholder="Nome do Produto"
              className="search-input"
            />
          </div>

          <div className="form-row">
            <input
              type="text"
              value={codCategoria}
              onChange={(e) => setCodCategoria(e.target.value)}
              placeholder="Código Categoria"
              className="search-input"
            />
            <input
              type="text"
              value={nomeCategoria}
              onChange={(e) => setNomeCategoria(e.target.value)}
              placeholder="Categoria"
              className="search-input"
            />
            <input
              type="text"
              value={prioridade}
              onChange={(e) => setPrioridade(e.target.value)}
              placeholder="Prioridade"
              className="search-input"
            />
            <input
              type="text"
              value={loja}
              onChange={(e) => setLoja(e.target.value)}
              placeholder="Loja Solicitante"
              className="search-input"
            />
            <input
              type="text"
              value={requisitante}
              onChange={(e) => setRequisitante(e.target.value)}
              placeholder="Requisitante"
              className="search-input"
            />
          </div>

          <div className="form-row">
            <select
              value={origem}
              onChange={(e) => setOrigem(e.target.value as '' | 'PLANEJAMENTO' | 'BALCAO')}
              className="form-select"
            >
              <option value="">Requisitado por...</option>
              <option value="PLANEJAMENTO">Planejamento</option>
              <option value="BALCAO">Balcão</option>
            </select>
          </div>

          <div className="form-row">
            <input
              type="text"
              value={contato}
              onChange={(e) => setContato(e.target.value)}
              placeholder="Contato"
              className="search-input"
            />
            <input
              type="text"
              value={nomeConsultor}
              onChange={(e) => setNomeConsultor(e.target.value)}
              placeholder="Consultor"
              className="search-input"
            />
            <input
              type="text"
              value={codCliente}
              onChange={(e) => setCodCliente(e.target.value)}
              placeholder="Código Cliente"
              className="search-input"
            />
            <input
              type="text"
              value={nomeCliente}
              onChange={(e) => setNomeCliente(e.target.value)}
              placeholder="Nome Cliente"
              className="search-input"
            />
          </div>

          <div className="form-row">
            <input
              type="text"
              value={preOrdemId}
              onChange={(e) => setPreOrdemId(e.target.value)}
              placeholder="Cotação"
              className="search-input"
            />
            <input
              type="text"
              value={codFornecedor}
              onChange={(e) => setCodFornecedor(e.target.value)}
              placeholder="Código Fornecedor"
              className="search-input"
            />
            <input
              type="text"
              value={nomeFornecedor}
              onChange={(e) => setNomeFornecedor(e.target.value)}
              placeholder="Nome Fornecedor"
              className="search-input"
            />
            <input
              type="text"
              value={lojaEntrega}
              onChange={(e) => setLojaEntrega(e.target.value)}
              placeholder="Loja de Entrega"
              className="search-input"
            />
            <input
              type="text"
              value={ordemCompraId}
              onChange={(e) => setOrdemCompraId(e.target.value)}
              placeholder="Ordem de Compra"
              className="search-input"
            />
          </div>

          <div className="form-row">
            <DateInput
              value={dataEntrega}
              onChange={setDataEntrega}
              placeholder="Data Entrega"
              purpose="Previsão de entrega"
              theme={theme}
            />
            <DateInput
              value={dataEncerrada}
              onChange={setDataEncerrada}
              placeholder="Data Encerrada"
              purpose="Encerrada até"
              theme={theme}
            />
          </div>

          <div className="form-row">
            <input
              type="text"
              value={conferenciaId}
              onChange={(e) => setConferenciaId(e.target.value)}
              placeholder="Conferência"
              className="search-input"
            />
            <input
              type="text"
              value={cnpjFornecedor}
              onChange={(e) => setCnpjFornecedor(e.target.value)}
              placeholder="CNPJ Fornecedor"
              className="search-input"
            />
            <input
              type="text"
              value={nomeComprador}
              onChange={(e) => setNomeComprador(e.target.value)}
              placeholder="Comprador"
              className="search-input"
            />
            <input
              type="text"
              value={nroNotaFiscal}
              onChange={(e) => setNroNotaFiscal(e.target.value)}
              placeholder="Nº Nota Fiscal"
              className="search-input"
            />
            <input
              type="text"
              value={nomeConferente}
              onChange={(e) => setNomeConferente(e.target.value)}
              placeholder="Conferente"
              className="search-input"
            />
          </div>
        </div>

        <SearchButton isLoading={isLoading} onClick={handleSearch} disabled={isLoading}>
          Procurar
        </SearchButton>
      </div>
    </div>
  );
};

export default AdvancedSearchConferenciaKanbanModalContent;
